export interface SuppressionFormState {
  org: string;
  reviewer: string;
  match_kind: 'token_overlap';
  tokensInput: string;
  min_overlap: string;
  rationale: string;
  source_ref: string;
  expires_at: string;
}

export type ValidationResult =
  | { ok: true }
  | { ok: false; errors: Partial<Record<keyof SuppressionFormState, string>> };

export interface CreateBody {
  org: string;
  reviewer: string;
  match_kind: 'token_overlap';
  tokens: string[];
  rationale: string;
  min_overlap?: number;
  source_ref?: string;
  expires_at?: string;
}

export function parseTokensInput(input: string): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of input.split(',')) {
    const t = raw.trim();
    if (t.length > 0 && !seen.has(t)) {
      seen.add(t);
      result.push(t);
    }
  }
  return result;
}

export function validateSuppressionForm(state: SuppressionFormState): ValidationResult {
  const errors: Partial<Record<keyof SuppressionFormState, string>> = {};

  if (!state.org.trim()) {
    errors['org'] = 'Organisation is required';
  }

  if (!state.reviewer.trim()) {
    errors['reviewer'] = 'Reviewer is required';
  }

  const tokens = parseTokensInput(state.tokensInput);
  if (tokens.length === 0) {
    errors['tokensInput'] = 'At least one token is required';
  }

  if (state.rationale.trim().length < 10) {
    errors['rationale'] = 'Rationale must be at least 10 characters';
  }

  if (state.min_overlap !== '') {
    const n = Number(state.min_overlap);
    if (!Number.isInteger(n) || n <= 0) {
      errors['min_overlap'] = 'min_overlap must be a positive integer';
    } else if (n > tokens.length) {
      errors['min_overlap'] = `min_overlap (${n}) cannot exceed token count (${tokens.length})`;
    }
  }

  if (state.expires_at !== '') {
    const d = new Date(state.expires_at);
    if (isNaN(d.getTime())) {
      errors['expires_at'] = 'Must be a valid date';
    }
  }

  if (Object.keys(errors).length > 0) {
    return { ok: false, errors };
  }
  return { ok: true };
}

export function isSafeUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

export function buildCreateBody(state: SuppressionFormState): CreateBody {
  const tokens = parseTokensInput(state.tokensInput);
  const body: CreateBody = {
    org: state.org,
    reviewer: state.reviewer,
    match_kind: 'token_overlap',
    tokens,
    rationale: state.rationale,
  };
  if (state.min_overlap !== '') {
    body.min_overlap = Number(state.min_overlap);
  }
  if (state.source_ref.trim() !== '') {
    body.source_ref = state.source_ref.trim();
  }
  if (state.expires_at !== '') {
    body.expires_at = `${state.expires_at}T23:59:59.000Z`;
  }
  return body;
}
